import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Upload, 
  InputNumber, 
  DatePicker, 
  Row, 
  Col, 
  message, 
  Spin, 
  Steps,
  Divider,
  Space,
  Image,
  Select,
  Modal
} from 'antd';
import { 
  UploadOutlined, 
  InboxOutlined, 
  DeleteOutlined,
  SaveOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getShipment, updateShipment, deleteShipment } from '../services/shipmentService';
import { getConfig } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { debounce } from 'lodash';
import locale from 'antd/es/date-picker/locale/zh_CN';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

// 运单状态选项
const STATUS_OPTIONS = [
  { value: 'pending', label: '待处理' },
  { value: 'instock', label: '已入库' },
  { value: 'shipped', label: '已出库' },
  { value: 'delivered', label: '已签收' },
  { value: 'completed', label: '已完成' }
];

const EditShipment = () => {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  
  // 状态
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [cargoImages, setCargoImages] = useState([]);
  const [packingImages, setPackingImages] = useState([]);
  const [shippingImages, setShippingImages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [currentImageType, setCurrentImageType] = useState('cargo'); // 当前图片类型
  
  // API基础URL
  const baseUrl = getConfig().apiBaseUrl || 'https://warehouseapi.proxypool.eu.org';
  
  // 获取运单信息
  useEffect(() => {
    if (!shipmentId) return;
    
    const fetchShipment = async () => {
      try {
        setLoading(true);
        console.log('开始获取运单ID:', shipmentId);
        
        // 使用现有的api实例获取数据，避免CORS问题
        const response = await api.get(`/shipment?id=${shipmentId}`);
        const data = response.data;
        console.log('获取运单响应:', data);
        
        // 处理响应数据
        let shipmentData = null;
        
        if (data.success) {
          // 尝试不同的数据结构
          if (data.shipment) {
            shipmentData = data.shipment;
          } else if (data.data) {
            if (Array.isArray(data.data)) {
              // 如果是数组，找到匹配的ID
              shipmentData = data.data.find(item => item.id === parseInt(shipmentId) || item.id === shipmentId);
            } else if (typeof data.data === 'object') {
              shipmentData = data.data;
            }
          }
        }
        
        if (shipmentData) {
          console.log('找到运单数据:', shipmentData);
          setShipment(shipmentData);
          
          // 设置表单初始值
          form.setFieldsValue({
            date: shipmentData.date ? moment(shipmentData.date) : null,
            name: shipmentData.name,
            phone: shipmentData.phone,
            tracking_no: shipmentData.tracking_no,
            cost: shipmentData.cost,
            country: shipmentData.country,
            status: shipmentData.status || 'pending'
          });
          
          // 设置图片
          if (shipmentData.image_urls) {
            // 设置货物照片
            if (shipmentData.image_urls.cargo) {
              const cargoFileList = shipmentData.image_urls.cargo.map((url, index) => {
                // 处理URL
                let fileUrl = url;
                
                // 如果是完整URL但指向错误的服务器
                if (url.includes('shipments-worker-test.stawky.workers.dev/images/')) {
                  // 提取文件名并使用正确的图片服务器
                  const fileName = url.split('/').pop();
                  fileUrl = `https://imgapi.proxypool.eu.org/${fileName}`;
                }
                // 如果不是完整URL
                else if (!url.startsWith('http')) {
                  fileUrl = `https://imgapi.proxypool.eu.org/${url}`;
                }
                
                return {
                  uid: `cargo-${index}`,
                  name: url.split('/').pop(),
                  status: 'done',
                  url: fileUrl,
                  thumbUrl: fileUrl,
                  originFileObj: {
                    url: fileUrl
                  }
                };
              });
              setCargoImages(cargoFileList);
            }
            
            // 设置打包照片
            if (shipmentData.image_urls.packing) {
              const packingFileList = shipmentData.image_urls.packing.map((url, index) => {
                // 处理URL
                let fileUrl = url;
                
                // 如果是完整URL但指向错误的服务器
                if (url.includes('shipments-worker-test.stawky.workers.dev/images/')) {
                  // 提取文件名并使用正确的图片服务器
                  const fileName = url.split('/').pop();
                  fileUrl = `https://imgapi.proxypool.eu.org/${fileName}`;
                }
                // 如果不是完整URL
                else if (!url.startsWith('http')) {
                  fileUrl = `https://imgapi.proxypool.eu.org/${url}`;
                }
                
                return {
                  uid: `packing-${index}`,
                  name: url.split('/').pop(),
                  status: 'done',
                  url: fileUrl,
                  thumbUrl: fileUrl,
                  originFileObj: {
                    url: fileUrl
                  }
                };
              });
              setPackingImages(packingFileList);
            }
            
            // 设置物流照片
            if (shipmentData.image_urls.shipping) {
              const shippingFileList = shipmentData.image_urls.shipping.map((url, index) => {
                // 处理URL
                let fileUrl = url;
                
                // 如果是完整URL但指向错误的服务器
                if (url.includes('shipments-worker-test.stawky.workers.dev/images/')) {
                  // 提取文件名并使用正确的图片服务器
                  const fileName = url.split('/').pop();
                  fileUrl = `https://imgapi.proxypool.eu.org/${fileName}`;
                }
                // 如果不是完整URL
                else if (!url.startsWith('http')) {
                  fileUrl = `https://imgapi.proxypool.eu.org/${url}`;
                }
                
                return {
                  uid: `shipping-${index}`,
                  name: url.split('/').pop(),
                  status: 'done',
                  url: fileUrl,
                  thumbUrl: fileUrl,
                  originFileObj: {
                    url: fileUrl
                  }
                };
              });
              setShippingImages(shippingFileList);
            }
          }
          
          // 根据状态设置当前步骤
          const status = shipmentData.status || 'pending';
          switch (status) {
            case 'pending':
              setCurrentStep(0);
              break;
            case 'instock':
              setCurrentStep(1);
              break;
            case 'shipped':
              setCurrentStep(2);
              break;
            case 'delivered':
            case 'completed':
              setCurrentStep(3);
              break;
            default:
              setCurrentStep(0);
          }
        } else {
          message.warning(`未找到ID为 ${shipmentId} 的运单`);
        }
      } catch (error) {
        console.error('获取运单详情错误:', error);
        message.error('获取运单信息失败: ' + (error.message || '未知错误'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchShipment();
  }, [shipmentId, form, baseUrl]);
  
  // 监听粘贴事件
  useEffect(() => {
    const handlePaste = async (event) => {
      // 检查是否有图片数据
      if (!event.clipboardData || !event.clipboardData.items) return;
      
      // 查找粘贴的图片
      const items = Array.from(event.clipboardData.items);
      const imageItem = items.find(item => item.type.startsWith('image/'));
      
      if (imageItem) {
        // 阻止默认粘贴行为
        event.preventDefault();
        
        // 获取图片文件
        const file = imageItem.getAsFile();
        if (!file) return;
        
        // 根据当前图片类型决定上传到哪个分类
        try {
          message.loading('正在处理粘贴的图片...', 0.5);
          
          // 创建上传文件对象
          const uploadFile = {
            uid: Date.now(),
            name: `paste-${Date.now()}.${file.type.split('/')[1] || 'png'}`,
            status: 'done',
            url: URL.createObjectURL(file),
            originFileObj: file
          };
          
          // 根据当前图片类型更新对应的图片列表
          switch (currentImageType) {
            case 'cargo':
              setCargoImages(prev => [...prev, uploadFile]);
              break;
            case 'packing':
              setPackingImages(prev => [...prev, uploadFile]);
              break;
            case 'shipping':
              setShippingImages(prev => [...prev, uploadFile]);
              break;
            default:
              setCargoImages(prev => [...prev, uploadFile]);
          }
          
          message.success('图片粘贴成功');
          
        } catch (error) {
          console.error('处理粘贴图片失败:', error);
          message.error('处理粘贴图片失败');
        }
      }
    };
    
    // 添加事件监听
    document.addEventListener('paste', handlePaste);
    
    // 清除事件监听
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [currentImageType]); // 依赖当前图片类型

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      
      // 设置保存状态
      setSaving(true);
      
      // 准备表单数据
      const formData = new FormData();
      formData.append('id', shipmentId);
      formData.append('date', values.date ? values.date.format('YYYY-MM-DD') : '');
      formData.append('name', values.name || '');
      formData.append('phone', values.phone || '');
      formData.append('tracking_no', values.tracking_no || '');
      formData.append('cost', values.cost || 0);
      formData.append('country', values.country || '未知国家');
      formData.append('status', values.status || 'pending');
      
      // 处理现有图片
      // 货物照片
      cargoImages.forEach(image => {
        if (image.originFileObj && !image.originFileObj.url) {
          // 新上传的图片
          formData.append('cargo', image.originFileObj);
          console.log('新上传货物图片:', image.name);
        } else {
          // 已有的图片
          const fileName = image.url ? image.url.split('/').pop() : image.name;
          formData.append('keep_cargo', fileName);
          console.log('保留货物图片:', fileName);
        }
      });
      
      // 打包照片
      packingImages.forEach(image => {
        if (image.originFileObj && !image.originFileObj.url) {
          // 新上传的图片
          formData.append('packing', image.originFileObj);
          console.log('新上传打包图片:', image.name);
        } else {
          // 已有的图片
          const fileName = image.url ? image.url.split('/').pop() : image.name;
          formData.append('keep_packing', fileName);
          console.log('保留打包图片:', fileName);
        }
      });
      
      // 物流照片
      shippingImages.forEach(image => {
        if (image.originFileObj && !image.originFileObj.url) {
          // 新上传的图片
          formData.append('shipping', image.originFileObj);
          console.log('新上传物流图片:', image.name);
        } else {
          // 已有的图片
          const fileName = image.url ? image.url.split('/').pop() : image.name;
          formData.append('keep_shipping', fileName);
          console.log('保留物流图片:', fileName);
        }
      });
      
      // 发送更新请求
      const result = await updateShipment(formData);
      
      if (result.success) {
        message.success('运单更新成功');
        // 导航回查询页面
        navigate('/query');
      } else {
        message.error('更新失败: ' + (result.error || '未知错误'));
      }
    } catch (error) {
      message.error('表单验证或提交错误: ' + (error.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };
  
  // 图片上传前处理（压缩）
  const handleImageUpload = async (file, fileList, setFileList) => {
    // 检查文件类型
    const isAllowedType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!isAllowedType) {
      message.error('只能上传JPG/PNG/WebP格式的图片!');
      return Upload.LIST_IGNORE;
    }
    
    // 检查文件大小
    const isLessThan2M = file.size / 1024 / 1024 < 2;
    if (!isLessThan2M) {
      message.error('图片必须小于2MB!');
      return Upload.LIST_IGNORE;
    }
    
    // 添加到文件列表
    const newFileList = [...fileList];
    newFileList.push({
      uid: file.uid,
      name: file.name,
      status: 'done',
      originFileObj: file
    });
    
    setFileList(newFileList);
    return false;
  };
  
  // 图片上传前处理（压缩）- 货物照片
  const beforeUploadCargo = async (file) => {
    return handleImageUpload(file, cargoImages, setCargoImages);
  };
  
  // 处理删除运单
  const handleDeleteShipment = async () => {
    try {
      setDeleting(true);
      
      const result = await deleteShipment(shipmentId);
      
      if (result.success) {
        message.success('运单删除成功');
        // 导航回查询页面
        navigate('/query');
      } else {
        message.error('删除失败: ' + (result.error || '未知错误'));
      }
    } catch (error) {
      message.error('删除运单错误: ' + (error.message || '未知错误'));
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
    }
  };
  
  // 显示删除确认对话框
  const showDeleteConfirm = () => {
    setDeleteModalVisible(true);
  };

  // 图片上传前处理（压缩）- 打包照片
  const beforeUploadPacking = async (file) => {
    return handleImageUpload(file, packingImages, setPackingImages);
  };

  // 图片上传前处理（压缩）- 物流照片
  const beforeUploadShipping = async (file) => {
    return handleImageUpload(file, shippingImages, setShippingImages);
  };
  
  // 图片预览
  const handlePreview = (file) => {
    let previewImage = file.url || file.thumbUrl;
    
    Modal.info({
      title: file.name || '图片预览',
      width: '80%',
      maskClosable: true,
      content: (
        <div style={{ textAlign: 'center' }}>
          <img alt="预览" style={{ maxWidth: '100%' }} src={previewImage} />
        </div>
      )
    });
  };
  
  // 移除图片
  const handleRemove = (file, fileList, setFileList) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
  };
  
  // 步骤变更
  const handleStepChange = (step) => {
    setCurrentStep(step);
    
    // 根据步骤更新状态
    let status = 'pending';
    switch (step) {
      case 0:
        status = 'pending';
        break;
      case 1:
        status = 'instock';
        break;
      case 2:
        status = 'shipped';
        break;
      case 3:
        status = 'delivered';
        break;
      default:
        status = 'pending';
    }
    
    form.setFieldsValue({ status });
  };

  // 设置当前图片类型
  const handleImageTypeChange = (type) => {
    setCurrentImageType(type);
  };
  
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>加载运单信息...</p>
      </div>
    );
  }
  
  return (
    <Card title={<Title level={3}>编辑运单 #{shipmentId}</Title>}>
      <Steps
        current={currentStep}
        onChange={handleStepChange}
        items={[
          { title: '待处理', description: '运单创建' },
          { title: '已入库', description: '货物到达' },
          { title: '已出库', description: '打包完成' },
          { title: '已签收', description: '物流完成' }
        ]}
      />
      
      <Divider />
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          date: moment(),
          country: '未知国家',
          status: 'pending'
        }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="date"
              label="日期"
              rules={[{ required: true, message: '请选择日期' }]}
            >
              <DatePicker locale={locale} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="status"
              label="运单状态"
              rules={[{ required: true, message: '请选择运单状态' }]}
            >
              <Select>
                {STATUS_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="country"
              label="目的国家"
            >
              <Input placeholder="目的国家" />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="name"
              label="客户姓名"
              rules={[{ required: true, message: '请输入客户姓名' }]}
            >
              <Input placeholder="客户姓名" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="phone"
              label="电话"
              rules={[{ required: true, message: '请输入电话' }]}
            >
              <Input placeholder="电话" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="tracking_no"
              label="物流单号"
            >
              <Input placeholder="物流单号" />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="cost"
              label="运费"
              rules={[{ required: true, message: '请输入运费' }]}
            >
              <InputNumber
                placeholder="运费"
                style={{ width: '100%' }}
                min={0}
                precision={2}
                step={10}
              />
            </Form.Item>
          </Col>
        </Row>
        
        <Divider orientation="left">货物照片 (支持直接粘贴图片)</Divider>
        <Form.Item>
          <Upload
            listType="picture-card"
            fileList={cargoImages}
            beforeUpload={beforeUploadCargo}
            onPreview={handlePreview}
            onRemove={file => handleRemove(file, cargoImages, setCargoImages)}
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess('ok'), 0)}
            onClick={() => handleImageTypeChange('cargo')}
          >
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>点击上传或粘贴图片</div>
            </div>
          </Upload>
        </Form.Item>
        
        <Divider orientation="left">打包照片 (支持直接粘贴图片)</Divider>
        <Form.Item>
          <Upload
            listType="picture-card"
            fileList={packingImages}
            beforeUpload={beforeUploadPacking}
            onPreview={handlePreview}
            onRemove={file => handleRemove(file, packingImages, setPackingImages)}
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess('ok'), 0)}
            onClick={() => handleImageTypeChange('packing')}
          >
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>点击上传或粘贴图片</div>
            </div>
          </Upload>
        </Form.Item>
        
        <Divider orientation="left">物流照片 (支持直接粘贴图片)</Divider>
        <Form.Item>
          <Upload
            listType="picture-card"
            fileList={shippingImages}
            beforeUpload={beforeUploadShipping}
            onPreview={handlePreview}
            onRemove={file => handleRemove(file, shippingImages, setShippingImages)}
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess('ok'), 0)}
            onClick={() => handleImageTypeChange('shipping')}
          >
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>点击上传或粘贴图片</div>
            </div>
          </Upload>
        </Form.Item>
        
        <Form.Item>
          <Space>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit} loading={saving}>
              保存
            </Button>
            <Button onClick={() => navigate('/query')}>
              返回
            </Button>
            <Button 
              type="danger" 
              icon={<DeleteOutlined />} 
              onClick={showDeleteConfirm}
              style={{ backgroundColor: '#ff4d4f', color: 'white' }}
            >
              删除运单
            </Button>
          </Space>
        </Form.Item>
      </Form>
      {/* 删除确认对话框 */}
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={handleDeleteShipment}
        onCancel={() => setDeleteModalVisible(false)}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ loading: deleting, danger: true }}
      >
        <p>确定要删除运单 #{shipmentId} 吗？</p>
        <p>此操作不可逆，删除后数据将无法恢复。</p>
      </Modal>
    </Card>
  );
};

export default EditShipment;